import { Editor } from '@tiptap/core';
import { BubbleMenu as TiptapBubbleMenu } from '@tiptap/react';
import { useEffect, useRef, useState } from 'react';

import { Box, CheckOutline, HStack, IconButton, Input, Link, PencilOutline, Tooltip, TrashOutline, rem } from '@bonusly/design-system';

interface LinkBubbleMenuProps {
	editor: Editor;
}

// Check if cursor/selection is inside a link by walking document nodes.
function isInLink(editor: Editor): boolean {
	const { from, to } = editor.state.selection;
	const { doc } = editor.state;
	let found = false;

	const isCollapsed = from === to;
	const searchFrom = isCollapsed ? Math.max(0, from - 1) : from;
	const searchTo = isCollapsed ? Math.min(doc.content.size, to + 1) : to;

	doc.nodesBetween(searchFrom, searchTo, (node, pos) => {
		if (!node.isText) return;

		const nodeStart = pos;
		const nodeEnd = pos + node.nodeSize;
		// Only treat collapsed cursors as "in link" when they are strictly inside
		// the text node, not on its start or end boundaries.
		const cursorStrictlyInsideNode = from > nodeStart && from < nodeEnd;
		const selectionOverlaps = !isCollapsed && from < nodeEnd && to > nodeStart;

		if (cursorStrictlyInsideNode || selectionOverlaps) {
			if (node.marks.some((mark) => mark.type.name === 'link')) {
				found = true;
				return false;
			}
		}
	});

	return found;
}

function getLinkHref(editor: Editor): string | null {
	const { from, to } = editor.state.selection;
	const { doc } = editor.state;
	let href: string | null = null;

	const isCollapsed = from === to;
	const searchFrom = isCollapsed ? Math.max(0, from - 1) : from;
	const searchTo = isCollapsed ? Math.min(doc.content.size, to + 1) : to;

	doc.nodesBetween(searchFrom, searchTo, (node, pos) => {
		if (!node.isText) return;

		const nodeStart = pos;
		const nodeEnd = pos + node.nodeSize;
		const cursorStrictlyInsideNode = from > nodeStart && from < nodeEnd;
		const selectionOverlaps = !isCollapsed && from < nodeEnd && to > nodeStart;

		if (cursorStrictlyInsideNode || selectionOverlaps) {
			for (const mark of node.marks) {
				if (mark.type.name === 'link' && mark.attrs.href) {
					href = mark.attrs.href;
					return false;
				}
			}
		}
	});

	return href;
}

export const LinkBubbleMenu = ({ editor }: LinkBubbleMenuProps) => {
	const [linkEditMode, setLinkEditMode] = useState<'view' | 'edit'>('view');
	const [, forceUpdate] = useState(0);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		const handleSelectionUpdate = () => {
			forceUpdate((n) => n + 1);
			setLinkEditMode('view');
		};

		const handleTransaction = () => {
			forceUpdate((n) => n + 1);
		};

		editor.on('selectionUpdate', handleSelectionUpdate);
		editor.on('transaction', handleTransaction);

		return () => {
			editor.off('selectionUpdate', handleSelectionUpdate);
			editor.off('transaction', handleTransaction);
		};
	}, [editor]);

	const isLinkActive = isInLink(editor);
	const linkHref = isLinkActive ? getLinkHref(editor) : null;
	const isEditMode = linkEditMode === 'edit';

	const handleApplyLink = (e?: React.FormEvent | React.MouseEvent) => {
		e?.preventDefault();

		let url = inputRef.current?.value ?? '';
		if (!url) {
			editor.chain().focus().extendMarkRange('link').unsetLink().run();
			setLinkEditMode('view');
			return;
		}

		if (!url.startsWith('http://') && !url.startsWith('https://')) {
			url = `https://${url}`;
		}

		editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
		setLinkEditMode('view');
	};

	const handleEditLink = () => {
		setLinkEditMode('edit');
		setTimeout(() => {
			if (!inputRef.current) return;
			inputRef.current.value = linkHref ?? '';
			inputRef.current.focus();
			inputRef.current.select();
		}, 0);
	};

	const handleRemoveLink = () => {
		editor.chain().focus().extendMarkRange('link').unsetLink().run();
		setLinkEditMode('view');
	};

	const handleInputKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Escape') {
			setLinkEditMode('view');
		}
	};

	const handleOpenLink = (e: React.MouseEvent<HTMLAnchorElement>) => {
		if (!linkHref) return;
		e.preventDefault();
		window.open(linkHref, '_blank', 'noopener,noreferrer');
	};

	return (
		<TiptapBubbleMenu
			editor={editor}
			shouldShow={({ editor }) => {
				return isInLink(editor);
			}}
			tippyOptions={{
				placement: 'top',
			}}
		>
			<Box bg="bg.inset" borderRadius="md" boxShadow="lg" p={1}>
				{isEditMode ? (
					<HStack alignItems="stretch" gap={rem(2)} width="full">
						<Box as="form" flex={1} onSubmit={handleApplyLink}>
							<Input
								border="none"
								boxShadow="none"
								color="text.default"
								fontSize="sm"
								display="flex"
								height="100%"
								onKeyDown={handleInputKeyDown}
								outline="none"
								placeholder="Enter URL..."
								ref={inputRef}
								w="full"
								minW="180px"
								_focus={{
									boxShadow: 'none',
									outline: 'none',
								}}
								_placeholder={{
									color: 'text.softer',
								}}
							/>
						</Box>

						<Tooltip label="Apply link" placement="top" hasArrow>
							<IconButton
								onClick={handleApplyLink}
								aria-label="Apply link"
								bg="none"
								borderRadius={4}
								boxSize={10}
								color="text.default"
								icon={<CheckOutline size="sm" />}
								minW="none"
								width="fit-content"
								height="fit-content"
								p={1.5}
								_hover={{
									bg: 'secondary.bgAlphaHover',
								}}
							/>
						</Tooltip>
					</HStack>
				) : (
					<HStack alignItems="center" gap={rem(2)} width="full">
						{linkHref ? (
							<Link
								href={linkHref}
								target="_blank"
								rel="noopener noreferrer"
								onClick={handleOpenLink}
								color="text.interactive"
								fontSize="sm"
								maxW="225px"
								overflow="hidden"
								textOverflow="ellipsis"
								whiteSpace="nowrap"
								display="block"
								textDecoration="underline"
								px={2}
								_hover={{ color: 'primary.fgStronger' }}
							>
								{linkHref}
							</Link>
						) : null}
						<Box flex={1} />

						<Tooltip label="Edit link" placement="top" hasArrow>
							<IconButton
								onClick={handleEditLink}
								aria-label="Edit link"
								bg="none"
								borderRadius={4}
								boxSize={10}
								height="fit-content"
								width="fit-content"
								minW="none"
								color="text.default"
								icon={<PencilOutline size="sm" />}
								p={1.5}
								_hover={{ bg: 'secondary.bgAlphaHover' }}
							/>
						</Tooltip>
						<Tooltip label="Remove link" placement="top" hasArrow>
							<IconButton
								onClick={handleRemoveLink}
								aria-label="Remove link"
								bg="none"
								borderRadius={4}
								boxSize={10}
								height="fit-content"
								width="fit-content"
								minW="none"
								color="text.default"
								icon={<TrashOutline size="sm" />}
								p={1.5}
								_hover={{ bg: 'secondary.bgAlphaHover' }}
							/>
						</Tooltip>
					</HStack>
				)}
			</Box>
		</TiptapBubbleMenu>
	);
};
